import React, {useEffect, useState} from 'react';
import Form, { Actions, Conditional, EmailAddressValidator, Password, RequiredValidator, Select, TextField } from '../../Components/Form';
import Card from '../../Components/Containers/Card';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSession } from '../../Components/Session';
import Icon from '../../Components/Icons/Icon';
import { useToasts } from '../../Components/Toasts';
import API from '../../Services/API';
import Loading from '../../Components/Loading';
import Recaptcha from '../../Services/Recaptcha';
import TagManager from '../../Components/Analytics/TagManager';
import ButtonBar from '../../Components/Button/ButtonBar';
// import VoucherDetails from './VoucherDetails';

const loadCountries = async () => await import('../../Data/countries.js');

const Login = () => {
    const {code: paramCode} = useParams();
    const code = paramCode ?? 'FREE-PRO';
    const {showError} = useToasts();
    const {login} = useSession();
    const [voucherLocked, setVoucherLocked] = useState(!!code);
    const [registering, setRegistering] = useState(false);

    const register = async (data) => {
        try {
            const captchaToken = await Recaptcha.getToken('register');
            data.captchaToken = captchaToken;

            return API.post('/users/register', data)
                .then((res) => {
                    waitForRegistrationComplete(res.userId, {email: data.email, password: data.password});
                })
                .catch(err => {
                    showError(API.parseErrorResponse(err));
                })
        } catch (err) {
            showError(API.parseErrorResponse(err));
        }
    }

    const waitForRegistrationComplete = (userId, credentials) => {
        setRegistering(true);

        API.get('/users/waitForRegistration?userId=' + userId).then(() => {
            login(credentials)
        }).catch(err => {
            showError(API.parseErrorResponse(err))
        })
    }

    useEffect(() => {
        Recaptcha.init();
    }, []);

    return <>
        <img src="/logo-name-horizontal.png"
            alt="getSoapy Logo"
            width="888"
            height="183"
            className="w-[75%] h-auto mx-auto mb-8"
        />

        <Card>
            {registering ? <Registering /> : <RegisterForm {...{code, register, voucherLocked, setVoucherLocked}} />}
        </Card>

        <TagManager />
    </>;
}

const RegisterForm = ({code, register, voucherLocked, setVoucherLocked}) => {
    const [isGB, setIsGB] = useState(true);
    const [countries, setCountries] = useState(null);
    const {showSuccess, showError} = useToasts();
    // const {experiments} = useSession();
    const navigate = useNavigate();

    useEffect(() => {
        loadCountries().then(module => {
            setCountries(module.default);
        });
    }, []);

    const submit = async (data) => {
        if (isGB) {
            return register(data);
        } else {
            return await API.post('/interests', data)
                .then(() => {
                    showSuccess('Thank you!', 'We\'ll let you know when we\'re available in your area.');
                })
                .catch(err => {
                    showError(API.parseErrorResponse(err));
                })
        }
    }
    const [searchParams] = useSearchParams();
    const emailQueryParam = searchParams.get('email') ?? null;

    return <>
        <ButtonBar
            className="mb-4"
            buttons={[
                {label: 'Login', onClick: () => navigate('/login')},
                {label: 'Register', selected: true, onClick: () => null},
            ]}
        />

        <Form data={{voucherCode: code, email: emailQueryParam, country: 'GB'}} onSubmit={submit}>

            {countries && <Select label="Country"
                name="country"
                options={Object.keys(countries).map(name => ({label: name, value: countries[name]}))}
                onChange={v => setIsGB(v === 'GB')}
                validators={[RequiredValidator]}
            />}
            {!countries && <Select label="Country"
                name="country"
                options={[{label: 'United Kingdom', value: 'GB'}]}
                disabled
            />}

            <Conditional condition={(data) => data.country !== 'GB'}>
                <div className="prose prose-sm p-2 bg-blue-50 rounded-lg">
                    <p>Sorry, we're only operating in the UK at the moment.</p>

                    <p>We plan to expand into other areas soon.</p>

                    <p>Would you like to be notified when we're available in your area?</p>
                </div>
                <TextField
                    name="name"
                    label="Name"
                    validators={isGB ? [] : [RequiredValidator]}
                />
            </Conditional>

            <TextField type="email"
                name="email"
                label="Email"
                validators={[RequiredValidator, EmailAddressValidator]}
            />
            <Conditional condition={(data) => data.country === 'GB'}>
                <Password label="Password"
                    name="password"
                    validators={isGB ? [RequiredValidator] : []}
                    showPrompts
                />
                <TextField label="Voucher code"
                    name="voucherCode"
                    validators={[]}
                    disabled={voucherLocked}
                    suffixOnClick={() => setVoucherLocked(false)}
                    suffix={voucherLocked ? <Icon icon="Pencil" /> : null}
                />
                {/* {experiments['ex-14'] === 'show' && <VoucherDetails />} */}
            </Conditional>
            <Actions submit={{
                label: isGB ? 'Register' : 'Keep me updated',
            }} />
        </Form>
    </>;
}

const Registering = () => {
    return <div className="my-4">
        <Loading size="2x" center />
        <p className="text-center text-muted text-sm mt-4">
            <strong>Welcome!</strong><br />
            Creating your account
        </p>
    </div>
}

export default Login;
